import request from "../shared/http";
import appConfig from "../shared/js/app-config";

async function unwrap(promise) {
  const res = await promise;
  return res?.data !== undefined ? res.data : res;
}

function sid() {
  return appConfig.getServiceId();
}

class EmailInboundService {
  async getConfig() {
    return unwrap(request.get(`/email-inbound/${sid()}/config`));
  }

  async updateConfig(payload) {
    return unwrap(request.put(`/email-inbound/${sid()}/config`, payload));
  }

  async listMailboxes() {
    return unwrap(request.get(`/email-inbound/${sid()}/mailboxes`));
  }

  async createMailbox(payload) {
    return unwrap(request.post(`/email-inbound/${sid()}/mailboxes`, payload));
  }

  async updateMailbox(id, payload) {
    return unwrap(request.put(`/email-inbound/${sid()}/mailboxes/${id}`, payload));
  }

  async deleteMailbox(id) {
    return unwrap(request.delete(`/email-inbound/${sid()}/mailboxes/${id}`));
  }

  async clearImap(mailboxId = null) {
    if (mailboxId) {
      return unwrap(request.delete(`/email-inbound/${sid()}/mailboxes/${mailboxId}/imap`));
    }
    return unwrap(request.delete(`/email-inbound/${sid()}/imap`));
  }

  async pollNewEmails(mailboxId = null) {
    const url = mailboxId
      ? `/email-inbound/${sid()}/mailboxes/${mailboxId}/poll`
      : `/email-inbound/${sid()}/poll`;
    return unwrap(request.post(url, {}));
  }

  async backfill({ range, unread, read, afterUid, mailboxId } = {}) {
    const url = mailboxId
      ? `/email-inbound/${sid()}/mailboxes/${mailboxId}/backfill`
      : `/email-inbound/${sid()}/backfill`;
    return unwrap(request.post(url, {
      range,
      unread,
      read,
      after_uid: afterUid,
    }));
  }

  async getSummary(period = "today") {
    return unwrap(request.get(`/email-inbound/${sid()}/summary?period=${encodeURIComponent(period)}`));
  }

  async getPollRuns({ page = 1, limit = 25, configId } = {}) {
    const params = new URLSearchParams({ page: String(page), limit: String(limit) });
    if (configId) params.set("config_id", configId);
    return unwrap(request.get(`/email-inbound/${sid()}/poll-runs?${params.toString()}`));
  }

  async getRules() {
    return unwrap(request.get(`/email-inbound/${sid()}/rules`));
  }

  async createRule(payload) {
    return unwrap(request.post(`/email-inbound/${sid()}/rules`, payload));
  }

  async updateRule(id, payload) {
    return unwrap(request.put(`/email-inbound/${sid()}/rules/${id}`, payload));
  }

  async deleteRule(id) {
    return unwrap(request.delete(`/email-inbound/${sid()}/rules/${id}`));
  }

  async getEvents({ page = 1, limit = 25, configId } = {}) {
    const params = new URLSearchParams({ page: String(page), limit: String(limit) });
    if (configId) params.set("config_id", configId);
    return unwrap(request.get(`/email-inbound/${sid()}/events?${params.toString()}`));
  }

  async getEvent(id) {
    return unwrap(request.get(`/email-inbound/${sid()}/events/${id}`));
  }

  async deleteEvent(id) {
    return unwrap(request.delete(`/email-inbound/${sid()}/events/${id}`));
  }

  async retryEvent(id) {
    return unwrap(request.post(`/email-inbound/${sid()}/events/${id}/retry`, {}));
  }

  async resendEvent(id) {
    return unwrap(request.post(`/email-inbound/${sid()}/events/${id}/resend`, {}));
  }

  async clearEvents() {
    return unwrap(request.delete(`/email-inbound/${sid()}/events`));
  }
}

export default new EmailInboundService();
